import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  model,
  ModelSignal,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, isEqual } from 'lodash-es';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs/internal/Subject';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { deleteImpMail } from 'src/app/STORE/actions/mailinfo.actions';
import { tableFieldsForDM } from 'src/app/STORE/selectors/services.selector';
import { ApiConstants } from 'src/app/base/api-constants';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { RegexPatternConstants } from 'src/app/base/regex-pattern.constants';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { nodeType } from 'src/app/pages/designs/data-model/data-model.component';
import { UtilService } from 'src/app/services/util.service';
import { v4 as uuidv4 } from 'uuid';
import { ChangeDetectorRef } from '@angular/core';
import { FsBaseService } from 'src/app/services/fs-base.service';

@Component({
  selector: 'vb-vs-import-prop',
  templateUrl: './vs-import-prop.component.html',
  styleUrls: ['./vs-import-prop.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [],
  standalone: false //NOSONAR
})
export class VsImportPropComponent implements OnInit, OnDestroy, OnChanges {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private onChange: (m: any) => void;
  private onTouched: (m: any) => void;
  private store = inject(Store);
  private messageService = inject(MessageService);
  private datePipe = inject(DatePipe);
  private util = inject(UtilService);
  private translate = inject(TranslateService);
  private baseService = inject(HttpBaseService);
  private cd = inject(ChangeDetectorRef);
  private appGlobalService = inject(AppGlobalService);
  private fsService = inject(FsBaseService);
  private allFields$ = new BehaviorSubject<any[]>([]);
  private imports$ = new BehaviorSubject<any[]>([]);

  @ViewChild('inputField') inputField!: ElementRef;
  @Input() element: any;
  @Input() disableField = false;
  @Input() formData: any;
  @Input() fieldIdMap: any = null;
  @Input() isRDBMS = false;
  @Input() settingsData: any = null;
  @Input() searchTerm = null;
  @Input() dataModelOrders = null;
  @Input() isBusinessRulesEnabled = false;
  imports: any = [];
  selected: string;
  targetBackup: any[] = [];
  fromSave = false;
  tableName = '';
  tableOrders: any[] = [];
  appId = this.appGlobalService.get('appId');
  orgId = this.appGlobalService.get('orgId');
  @Output() public onTemplateChange = new EventEmitter();

  @Input() pickList = null;
  srcFileTypes = [
    // {
    //   name: 'Google Sheet',
    //   value: 'google_sheet'
    // },
    {
      name: 'CSV',
      value: 'csv'
    },
    {
      name: 'Excel',
      value: 'excel'
    }
  ];
  recExistOptions = [
    {
      name: 'Update',
      value: 'update'
    },
    {
      name: 'Ignore',
      value: 'ignore'
    }
  ];
  runtimeBusinessRuleOption = [
    {
      name: 'No',
      value: 'No'
    },
    {
      name: 'Yes',
      value: 'Yes'
    },
    {
      name: 'Configure In Runtime',
      value: 'Configure In Runtime'
    }
  ];
  csvDelimiterOptions = [
    {
      name: 'comma (,)',
      value: 'comma'
    },
    {
      name: 'semicolon (;)',
      value: 'semicolon'
    },
    {
      name: 'tab (\t)',
      value: 'tab'
    },
    {
      name: 'space ( )',
      value: 'space'
    },
    {
      name: 'pipe (|)',
      value: 'pipe'
    }
  ];
  impsCloneCopy: any = [];
  allFields: any[] = [];
  source: any[] = [];
  target: any[] = [];
  showPickList = false;
  selectedPL: any[];
  currentImp: any = null;
  dateFormatOps: any = [];
  datetimeForOps: any = [];
  numForOptions: any = [];
  destroy$: Subject<boolean> = new Subject<boolean>();
  showLoader = false;
  private fsDataSub: Subscription | null = null;
  private initialCall = true;
  private batchSizeChange$ = new Subject<{ event: any; imp: any }>();

  get isBetaAccessPrivilegeConsumable(): boolean {
    return this.appGlobalService.betaAccessPrivilegeForApp;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setValues(value) {
    if (value) {
      try {
        this.imports = JSON.parse(value);
        this.impsCloneCopy = JSON.parse(value);
        window.dispatchEvent(new Event('resize'));
      } catch (e) {
        console.error(e);
      }
    }
  }

  getImpName(index) {
    let impNameAfterValidation = this.formData.name;
    if (this.formData.name.length > 55) {
      const result = this.formData.name.match(/.{1,55}/g) ?? [];
      impNameAfterValidation = result[0];
    }
    const impName = `${impNameAfterValidation} import ${index}`;
    const impProp = this.imports.find((im) => im.name.toLowerCase() === impName.toLowerCase());
    if (impProp) {
      return this.getImpName(index + 1);
    }
    return impName;
  }
  replaceUnderScoreWithSpace(str) {
    return str.replace(/_/g, ' ');
  }

  addImports() {
    const id = uuidv4();

    let impName = this.getImpName(this.imports.length + 1);
    impName = this.replaceUnderScoreWithSpace(impName);
    const impObj = {
      sid: id,
      name: impName,
      fields: this.allFields.map((obj) => {
        return { sid: obj.sid, name: obj.name, searchValue: obj.name };
      }),
      notifyMail: false,
      fileTypes: ['csv', 'excel'],
      csvDelimiter: 'comma',
      batchSize: 500,
      onImportErr: this.isRDBMS ? 'terminate_import' : 'exclude_error_rows_and_import',
      ifRecExist: 'ignore',
      dateFormat: this.normalizeFormatSelection(this.settingsData?.dateFormat),
      dateTimeFormat: this.normalizeFormatSelection(this.settingsData?.dateTimeFormat),
      runtimeBusinessRuleOption: '',
      numberFormat: null,
      roles: ['all'],
      err: false
    };
    this.selected = impObj.sid;
    this.imports = [...this.imports, impObj];
    const i = this.imports.length;
    // setTimeout(() => {
    //   $(`#impname-${i - 1}`).focus();
    // }, 100);
    this.setValueOnChanges(null, impObj, 'create');
  }

  delImp(event, i) {
    event.stopPropagation();
    this.confirmationDialogService.confirm({
      message: this.translate.instant('CONFIRMATION_MESSAGE_DELETE_IMPORT'),
      accept: () => {
        if (i > -1) {
          this.deleteImpMailTemplate(this.imports[i].mailTemplateId);
          const templateId = this.imports[i].sid;
          const api = {
            ...ApiConstants.deleteImportTemplates,
            url: ApiConstants.deleteImportTemplates.url.replace('{ids}', templateId)
          };
          this.baseService['delete'](api).subscribe({
            next: (response: any) => {
              this.showLoader = false;
            },
            error: (error: any) => {
              this.showLoader = false;
              this.showMessage('error', this.translate.instant('IMPORT_TEMPLATE_DELETE_FAILED'));
            }
          });
        }
      },
      reject: () => {
        // Rejected
      }
    });
  }


  assignTableOrders() {
    const tree = $.ui.fancytree.getTree('#tree');
    this.tableOrders = tree.rootNode?.children;
  }

  setValueOnChanges(event, imp, action?) {
    if (imp.name) {
      delete imp.err;
      imp.dateFormat = this.normalizeFormatSelection(imp.dateFormat);
      imp.dateTimeFormat = this.normalizeFormatSelection(imp.dateTimeFormat);
      imp.fields = [
        ...imp.fields.map((obj) => {
          if (obj.altName) {
            return { sid: obj.sid, altName: obj.altName };
          } else {
            return { sid: obj.sid };
          }
        })
      ];
      let data = this.imports.filter((imp) => imp.name);
      this.impsCloneCopy = cloneDeep(data);
      const payload = {
        ...imp,
        appId: this.appId,
        orgId: this.orgId,
        tableId: this.formData?.sid
      };
      this.handleNotifyMailPayload(payload, imp);

      if (action === 'create') {
        this.baseService['post'](ApiConstants.createImportTemplates, payload).subscribe({
          next: (createdImp: any) => {
            this.showLoader = false;
            // Replace the temp object with the one returned from backend (if needed)
            let idx = this.imports.findIndex((i) => i.sid === imp.sid);
            if (idx > -1) {
              this.imports[idx] = createdImp;
              this.impsCloneCopy = cloneDeep(this.imports);
            }
            this.onTemplateChange.emit({
              templateId: createdImp.sid,
              templateName: createdImp.name,
              action: 'create'
            });
          },
          error: () => {
            this.showLoader = false;
          }
        });
      } else {
        this.baseService['put'](ApiConstants.updateImportTemplates, payload).subscribe({
          next: (updatedImp: any) => {
            this.showLoader = false;
            let idx = this.imports.findIndex((i) => i.sid === updatedImp.sid);
            if (idx > -1) {
              this.imports[idx] = updatedImp;
              this.impsCloneCopy = cloneDeep(this.imports);
            }
          },
          error: () => {
            this.showLoader = false;
          }
        });
      }
    }
  }

  private validateImportName(imp: any): { isValid: boolean; errorMessage: string } {
    const myRe = RegexPatternConstants.alphanumericName;
    const array = myRe.exec(imp.name);
    if (!array) {
      return { isValid: false, errorMessage: this.translate.instant('ALPHANUMEREIC_CHARACTERS_ALLOWED') };
    }

    const duplicateIndex = this.imports.findIndex(
      (im) => im.sid !== imp.sid && im.name.toLowerCase() === imp.name.toLowerCase()
    );
    if (duplicateIndex > -1) {
      return { isValid: false, errorMessage: 'Import Name should be unique' };
    }

    return { isValid: true, errorMessage: '' };
  }

  private revertImportName(imp: any): void {
    const cloneIndex = this.impsCloneCopy.findIndex((im) => im.sid === imp.sid);
    if (cloneIndex > -1 && this.impsCloneCopy[cloneIndex].name) {
      const items = [...this.imports];
      items[cloneIndex].name = this.impsCloneCopy[cloneIndex].name;
      this.imports = [...items];
    }
  }

  private handleEmptyImportName(imp: any): void {
    const cloneIndex = this.impsCloneCopy.findIndex((i) => i.sid === imp.sid);
    if (cloneIndex > -1 && this.impsCloneCopy[cloneIndex].name) {
      const items = [...this.imports];
      items[cloneIndex].name = this.impsCloneCopy[cloneIndex].name;
      this.imports = [...items];
    } else {
      imp.err = true;
      this.updateImportsArr(imp);
    }
  }

  onNameChange(event, imp) {
    if (!imp.name) {
      this.showMessage('error', 'Import Name is required');
      this.handleEmptyImportName(imp);
      return;
    }

    const validation = this.validateImportName(imp);
    if (!validation.isValid) {
      this.showMessage('error', validation.errorMessage);
      this.revertImportName(imp);
      return;
    }

    this.setValueOnChanges(event, imp);
  }

  updateImportsArr(imp) {
    const updImports = this.imports.map((data) => {
      return data.sid === imp.sid ? imp : data;
    });
    this.imports = [...updImports];
  }

  handleNotifyMailPayload(payload: any, imp: any): void {
    if (imp.notifyMail) {
      return;
    }
    delete payload.mailTemplateId;
    this.deleteImpMailTemplate(imp.mailTemplateId);
  }

  deleteImpMailTemplate(mailTemplateId?: string): void {
    if (!mailTemplateId) {
      return;
    }
    this.store.dispatch(
      deleteImpMail({
        apiConstants: ApiConstants.deleteImpMail,
        ids: { ids: mailTemplateId, appId: this.appId }
      })
    );
  }

  onNotifyMailChangeConfirmation(event, imp) {
    const confirmUncheckCallback = () => {
      this.onNotifyMailChange(event, imp);
    };
    if (event.checked === false) {
      this.confirmationDialogService.confirm({
        message: this.translate.instant('MAIL_UNCHECK_CONFIRMATION'),
        accept: confirmUncheckCallback,
        reject: () => {
          imp.notifyMail = true;
          this.cd.detectChanges();
          return;
        }
      });
    }
  }

  onNotifyMailChange(event, imp) {
    if (imp.notifyMail) {
      imp.mailTemplateId = '';
    }
    this.updateImportsArr(imp);
    this.setValueOnChanges(event, imp);
  }

  onMailChanges(mailid, imp) {
    if (mailid) {
      imp.mailTemplateId = mailid;
      this.updateImportsArr(imp);
      this.setValueOnChanges(null, imp);
    }
  }

  handlefileTypeChange(event, imp) {
    if (imp.fileTypes.includes('csv')) {
      imp.csvDelimiter = 'comma';
    } else {
      imp.csvDelimiter = '';
    }
    this.updateImportsArr(imp);
    this.setValueOnChanges(event, imp);
  }

   increDecBatchSz(event, imp, operator) {
      switch (operator) {
        case '+':
          imp.batchSize += 10;
          break;
        case '-':
          imp.batchSize -= 10;
          break;
      }
      if (imp.batchSize > 1000) {
        imp.batchSize = 1000;
        this.showMessage('error', this.translate.instant('BATCH_SIZE_EXCEED_MAX', { name: imp.name }));
      } else if (imp.batchSize < 1) {
        imp.batchSize = 1;
        this.showMessage('error', this.translate.instant('BATCH_SIZE_BELOW_MIN', { name: imp.name }));
      }
      this.updateImportsArr(imp);
      this.batchSizeChange$.next({ event, imp });
      event.preventDefault();
      event.stopPropagation();
    }

    onbatchSzChange(event, imp) {
      if (imp.batchSize < 1) {
        imp.batchSize = 1;
        this.showMessage('error', this.translate.instant('BATCH_SIZE_BELOW_MIN', { name: imp.name }));
      } else if (imp.batchSize > 1000) {
        imp.batchSize = 1000;
        this.showMessage('error', this.translate.instant('BATCH_SIZE_EXCEED_MAX', { name: imp.name }));
      }
      this.updateImportsArr(imp);
      this.setValueOnChanges(event, imp);
    }




  onformatChanges(event, imp, format) {
    if (format === 'date') {
      imp.dateFormat = this.normalizeFormatSelection(imp.dateFormat);
    } else if (format === 'time') {
      imp.dateTimeFormat = this.normalizeFormatSelection(imp.dateTimeFormat);
    }
    this.updateImportsArr(imp);
    this.setValueOnChanges(event, imp);
  }

  showMessage(status: string, message: string, detail?: string) {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: status,
      summary: message,
      detail: detail
    });
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  togglePickList(event?: any, imp?: any) {
    if (event) {
      event.preventDefault();
      this.stopPropagation(event);
      const flds = cloneDeep(imp.fields);
      flds.forEach((i) => {
        if (i) {
          i.name = this.fieldIdMap[i.sid];
          i.searchValue = this.fieldIdMap[i.sid];
        }
        if (i.altName) {
          i.searchValue = `${i.name} ${i.altName}`;
        }
      });
      this.setValueToPickList(flds);
      this.target = [...this.selectedPL];
      this.source = this.allFields
        .filter((fld) => !this.target.find((f) => f.sid === fld.sid))
        .map((obj) => {
          if (obj.altName) {
            return { sid: obj.sid, name: obj.name, searchValue: `${obj.name}${obj.altName}` };
          }
          return { sid: obj.sid, name: obj.name, searchValue: obj.name };
        });
      this.targetBackup = [...this.target];
      this.currentImp = imp;
    } else {
      this.target = [];
      this.source = [];
      this.selectedPL = [];
    }
    this.showPickList = !this.showPickList;
  }

  setValueToPickList(value) {
    this.selectedPL = [];
    this.target = [];
    if (value.length > 0) {
      value.forEach((e) => {
        if (e) {
          e.allowEdit = true;
          this.target.push(e);
        }
      });
      this.source = this.allFields
        .filter((fld) => !this.target.find((f) => f.sid === fld.sid))
        .map((obj) => {
          if (obj.altName) {
            return { sid: obj.sid, name: obj.name, searchValue: `${obj.name}${obj.altName}` };
          }
          return { sid: obj.sid, name: obj.name, searchValue: obj.name };
        });
    } else {
      this.source = this.allFields.map((obj) => {
        return { sid: obj.sid };
      });
      this.target = [];
    }
    if (!this.target) {
      this.target = [];
    }
    this.selectedPL = [...this.target];
  }

  onMoveToTarget(event) {
    let items = [...this.target];
    items = items.map((item) => {
      item = { ...item, ...{ allowEdit: true } };
      return item;
    });
    this.target = [...items];
    this.source = [...this.source];
    this.selectedPL = [...this.target];
  }

  onMoveToSource(event) {
    const items = this.source.map((item) => {
      const obj = { ...item };
      delete obj.allowEdit;
      if (Object.prototype.hasOwnProperty.call(item, 'edit')) {
        delete obj.edit;
      }
      if (Object.prototype.hasOwnProperty.call(item, 'altName')) {
        delete obj.altName;
      }
      return obj;
    });
    this.source = [...items];
    this.target = [...this.target];
    this.selectedPL = [...this.target];
  }

  onTargetReorder(event) {
    this.source = [...this.source];
    this.target = [...this.target];
  }

  findField(sid, node, fld) {
    if (!node) {
      return [];
    }
    const res = node.find((node) => {
      if (!node.title) {
        return false;
      }
      if (node.type === nodeType.FIELD && node.data.sid === sid) {
        fld = node.data;
        return true;
      } else if (node.children) {
        return this.findField(sid, node.children, fld);
      }
    });
    return res;
  }

  savePicklist() {
    // Validate at least one field is selected
    if (!this.target || this.target.length === 0) {
      this.showMessage('error', this.translate.instant('NEED_ATLEAST_ONE_FIELD_IS_NECESSARY_TO_SAVE_IMPORT_TEMPLATE'));
      return;
    }
    
    const invalidFld = this.target.find((fld) => fld.err);
    if (invalidFld) {
      this.showMessage('error', 'Field Alternate Name should be unique');
      return;
    }
    this.selectedPL = this.target.map((ele) => {
      let obj = { ...ele };
      delete obj.allowEdit;
      if (Object.prototype.hasOwnProperty.call(ele, 'edit')) {
        delete obj.edit;
      }
      if (Object.prototype.hasOwnProperty.call(ele, 'name')) {
        delete obj.name;
      }
      if (Object.prototype.hasOwnProperty.call(ele, 'err')) {
        delete obj.err;
      }
      return obj;
    });
    this.currentImp = { ...this.currentImp, ...{ fields: this.selectedPL } };
    this.updateImportsArr(this.currentImp);
    this.setValueOnChanges(null, this.currentImp);
    this.fromSave = true;
    this.togglePickList(null, null);
    this.currentImp = null;
  }
  ngOnChanges(changes: any) {
    if (changes.isBusinessRulesEnabled) {
      const { currentValue: current, previousValue: previous } = changes.isBusinessRulesEnabled;
      if (!current && current !== previous && this.imports && this.imports.length > 0) {
        for (const imp of this.imports) {
          if (imp.runtimeBusinessRuleOption) {
            imp.runtimeBusinessRuleOption = '';
          }
        }
      }
    }
  }

  closeDialog(event) {
    if (!isEqual(this.target, this.targetBackup) && !this.fromSave) {
      this.confirmationDialogService.confirm({
        message: 'You have unsaved changes. Are you sure you want to leave this page?',
        accept: () => {
          this.target = [...this.targetBackup];
          this.showPickList = false;
        },
        reject: () => {
          this.showPickList = true;
        }
      });
    } else {
      this.showPickList = false;
    }
    this.fromSave = false;
  }

  editFieldName(property) {
    property.edit = true;
    if (!property.altName) {
      property.altName = '';
    }
    let items = [...this.target];
    items = items.map((item) => {
      return item.sid === property.sid ? property : item;
    });
    this.target = [...items];
    setTimeout(() => {
      if (property?.altName) {
        this.inputField.nativeElement.value = property.altName;
      }
    }, 100);
  }

  saveFieldName(property) {
    property.altName = this.inputField.nativeElement.value;
    const dupAltNme = this.target.find(
      (fld) => fld.sid !== property.sid && fld.altName && fld.altName.toLowerCase() === property.altName.toLowerCase()
    );
    if (dupAltNme) {
      property.err = true;
      this.showMessage(
        'error',
        `${property.altName} is already used as an alternate name. Alternate Name should be unique`
      );
      return;
    }
    property.err = false;
    property.edit = false;
    property.searchValue = `${property.name}${property.altName}`;

    let items = [...this.target];
    items = items.map((item) => {
      return item.sid === property.sid ? property : item;
    });
    this.target = [...items];
    this.selectedPL = [...this.target];
  }

  cancelEdit(property) {
    property.edit = false;
    delete property.altName;
    delete property.err;
    let items = [...this.target];
    items = items.map((item) => {
      return item.sid === property.sid ? property : item;
    });
    this.target = [...items];
  }

  ontargetSelect(event) {
    /* empty */
  }

  ngOnInit(): void {
    this.tableName = this.formData.name;
    this.initializeTableFields();
    this.initializeDateFormats();
    this.initializeNumberFormats();
    this.getDataFromFS();
    this.batchSizeChange$
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(({ event, imp }) => this.setValueOnChanges(event, imp));
  }

  private initializeTableFields(): void {
    this.store
      .select(tableFieldsForDM)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (!res || res.length === 0) {
          return;
        }
        this.allFields = this.filterValidFields(res);
        this.processLookupFields(res);
      });
  }

  private filterValidFields(fields: any[]): any[] {
    return fields.filter(
      (fld) =>
        fld.f_type !== 'Image Carousel' &&
        fld.f_type !== 'File Attachments' &&
        fld.f_type !== 'Complex Datatype' &&
        (!fld.f_sysF || this.isAllowedSystemField(fld.name)) &&
        fld.f_type !== 'Lookup' &&
        fld.f_transientField !== true
    );
  }

  private isAllowedSystemField(fieldName: string): boolean {
    const lowerName = fieldName.toLowerCase();
    return lowerName === 'pid' || lowerName === 'isactive' || lowerName === 'sid';
  }

  private processLookupFields(allFields: any[]): void {
    const lookupFields = allFields.filter((fld) => fld.f_type === 'Lookup');
    if (lookupFields.length === 0) {
      return;
    }

    const uniqueTableSids = this.getUniqueLookupTableSids(lookupFields);
    uniqueTableSids.forEach((fld) => this.fetchLookupTableData(fld, lookupFields));
  }

  private getUniqueLookupTableSids(lookupFields: any[]): any[] {
    return lookupFields.filter((fld, i) => lookupFields.findIndex((lf) => lf.f_lookupTo === fld.f_lookupTo) === i);
  }

  private fetchLookupTableData(fld: any, lookupFields: any[]): void {
    if (!fld.f_lookupTo) {
      return;
    }

    this.showLoader = true;
    this.baseService['get'](ApiConstants.getDatamodelTableData, { sid: fld.f_lookupTo })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.showLoader = false;
          this.handleLookupTableResponse(data, fld.f_lookupTo, lookupFields);
        },
        error: () => {
          this.showLoader = false;
        }
      });
  }

  private handleLookupTableResponse(data: any, lookupTo: string, lookupFields: any[]): void {
    if (!data?.t_funcPKey) {
      return;
    }
    const matchingFields = lookupFields.filter((f) => f.f_lookupTo === lookupTo);
    this.allFields = [...this.allFields, ...matchingFields];
  }

  private initializeDateFormats(): void {
    this.dateFormatOps = this.util.dateFormats.map((val) => ({
      label: `${val} (${this.datePipe.transform(new Date(), val)})`,
      value: val
    }));

    this.datetimeForOps = this.createDateTimeFormatOptions();
  }

  private initializeNumberFormats(): void {
    this.numForOptions = [
      {
        label: '9.999,00',
        value: {
          displayLabel: '9.999,00',
          thousandSeparator: '.',
          decimalSeperator: ','
        }
      },
      {
        label: '9,999.00',
        value: {
          displayLabel: '9,999.00',
          thousandSeparator: ',',
          decimalSeperator: '.'
        }
      }
    ];
  }

  private getDataFromFS(): void {
    const version = this.appGlobalService.get('appVersion');
    const queryObject = {
      collectionName: `ImportTemplate_${version}`,
      filter: [
        {
          field: 'appId',
          operator: '==',
          value: String(this.appId || '')
        },
        {
          field: 'tableId',
          operator: '==',
          value: String(this.formData?.sid || '')
        }
      ]
    };
    this.getImportData(queryObject);
  }

  private getImportData(queryObject: {
    collectionName: string;
    filter: { field: string; operator: string; value: string }[];
  }): void {
    try {
      if (this.fsDataSub && typeof (this.fsDataSub as any).unsubscribe === 'function') {
        (this.fsDataSub as any).unsubscribe();
      }

      this.fsDataSub = this.fsService.fireService(queryObject).subscribe(
        (res: any[]) => {
          if (res && res.length > 0) {
            const mapTemplate = (d: any): any => ({
              sid: d.sid,
              appId: d.appId,
              orgId: d.orgId,
              name: d.name,
              fields: d.fields,
              notifyMail: d.notifyMail,
              mailTemplateId: d.mailTemplateId,
              fileTypes: d.fileTypes,
              roles: d.roles,
              csvDelimiter: d.csvDelimiter,
              batchSize: d.batchSize,
              onImportErr: d.onImportErr,
              numberFormat: d.numberFormat,
              dateTimeFormat: this.normalizeFormatSelection(d.dateTimeFormat),
              dateFormat: this.normalizeFormatSelection(d.dateFormat),
              ifRecExist: d.ifRecExist,
              tableId: d.tableId,
              runtimeBusinessRuleOption: d.runtimeBusinessRuleOption
            });

            if (this.initialCall) {
              this.initialCall = false;
              const filteredRes = res.filter(
                (dt: any) => !dt.payload.doc.metadata.fromCache && !dt.payload.doc.data().recDeleted
              );
              const templates = filteredRes.map((obj: any) => mapTemplate(obj.payload.doc.data()));
              this.imports = templates;
              this.impsCloneCopy = cloneDeep(this.imports);
              this.imports$.next(this.imports);
            } else {
              const updated = [...this.imports];
              let hasChanges = false;
              res.forEach((dt: any) => {
                const docData = dt.payload.doc.data();
                const sid = docData.sid;
                const idx = updated.findIndex((t) => t.sid === sid);
                const NOT_FOUND = -1;

                if (docData.recDeleted) {
                  if (idx !== NOT_FOUND) {
                    updated.splice(idx, 1);
                    hasChanges = true;
                  }
                } else if (dt.payload.doc.exists || dt.type === 'added' || dt.type === 'modified') {
                  const newT = mapTemplate(docData);
                  if (idx !== NOT_FOUND) {
                    updated[idx] = { ...updated[idx], ...newT };
                    hasChanges = true;
                  } else {
                    updated.push(newT);
                    hasChanges = true;
                  }
                }
              });
              if (hasChanges) {
                this.imports = updated;
                this.impsCloneCopy = cloneDeep(this.imports);
                this.imports$.next(this.imports);
              }
            }
          }
        },
        (error) => {
          console.error(error);
        }
      );
    } catch (e) {
      console.warn(e);
    }
  }

  showErrorPopUp: boolean = false;

  toggleWarningPopup() {
    this.showErrorPopUp = !this.showErrorPopUp;
  }

  private createDateTimeFormatOptions(): { label: string; value: string }[] {
    return this.util.dateTimeFormats.map((val) => ({
      label: `${val} (${this.datePipe.transform(new Date(), val)})`,
      value: val
    }));
  }

  private normalizeFormatSelection(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      return [value];
    }

    return [];
  }

  validateFieldsInImport() {
    this.imports.forEach((imp) => {
      const idx = this.imports.findIndex((lk) => lk.sid === imp.sid);
      const invalidFields: string[] = [];
      imp.fields.forEach((fld) => {
        const exists = this.allFields.some((field) => field.sid === fld.sid);
        if (!exists) {
          invalidFields.push(fld.sid);
        }
      });
      this.util.handleDeletedIds(invalidFields, false, '', false, '', (message) => {
        this.imports[idx]['errorMsg'] = this.translate.instant('IMPORT_FIELDS_DELETED', { field: message });
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}

